<template>
  <div>
    <div style="background: #ffffff" v-if="!loading">
      <h3 v-if="title">{{ title }}</h3>
      <form @submit.prevent="compare" v-if="calculationValues">
        <slot name="input"
              :calculationValues="calculationValues"
              :overrides="overrides"
              :getRequiredFields="getRequiredFields"
              :getNotEditableFields="getNotEditableFields"
              :getOverridesValue="getOverridesValue"
              :editOverridesValue="editOverridesValue"
              :getPrettyName="getPrettyName"
              :getType="getType"
              :getErrors="getErrors"
              :getEnumOptions="getEnumOptions"
              :getSimplesFromCollection="getSimplesFromCollection"
              :getObjectsFromCollection="getObjectsFromCollection"
              :getArraysFromCollection="getArraysFromCollection"
        />
        <a-button type="primary" html-type="submit" style="margin-top: 1em;" :loading="loadingResult">
          Haal resultaten op
        </a-button>
      </form>

      <div>
        <div style="display:flex; flex-direction: row; justify-content: space-between; align-items: center; margin-bottom: 1em;">
          <h4 style="margin-bottom: 0">Resultaten</h4>
          <div v-if="!resultSaved">
            <a-tooltip>
              <template slot="title">
                <small>
                  Deze resultaten kunnen later gebruikt worden om klanten op de hoogte te stellen van eventuele besparingen.
                </small>
              </template>
              <a-button v-if="hasResultsWithoutIssues" @click="save" :loading="loadingSaving">
                Sla resultaten op
              </a-button>
            </a-tooltip>
          </div>
        </div>
        <slot name="pre-table" :result="result"></slot>
        <div v-if="loadingResult">
          laden...
        </div>
        <div v-else-if="noResultsFound">
          <div v-if="errorMessage" class="text-danger">
            <h5 class="text-danger">
              Er is iets fout gegaan, pas de ingevulde waarden aan en probeer het opnieuw.
            </h5>
          </div>
          <span v-else>
            Geen resultaten gevonden
          </span>
        </div>
        <div v-else>
          <a-alert class="text-warning" v-if="hasLastSavedResult"
                   :message="`Dit is het laatst opgeslagen resultaat. (${getResultCreatedAt} opgeslagen)`"
                   show-icon/>
          <a-alert class="text-warning" v-if="!resultCorrespondsWithOverrides"
                   message="Let op: het huidige resultaat komt niet overeen met de situatie hierboven. Klik op 'Haal resultaten op' voor de nieuwe situatie."
                   show-icon/>
        </div>
        <div v-if="result !== null && !loadingResult">
          <table class="table table-fixed table-striped table-bordered" style="table-layout: auto;">
            <thead>
            <tr>
              <th v-for="header in tableHeaders">{{ header.text }}</th>
              <th>Meer info</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(option, key) in getTableResult">
              <td v-for="header in tableHeaders">
                <slot :name="`table.${header.value}`" :value="option[header.value]" :option="option" :result="result">
                  {{ option[header.value] }}
                </slot>
              </td>
              <td>
                <a-button size="small" type="primary" @click="$set(allDataModal, key, true)">Meer info
                </a-button>
                <a-button size="small" type="danger" @click="$set(errorModal, key, true)"
                          v-if="option.hasOwnProperty(resultErrorsKey) && option[resultErrorsKey].length > 0">Problemen
                </a-button>
                <a-modal :visible="allDataModal[key]"
                         @cancel="$set(allDataModal, key, false)"
                         size="small"
                >
                  <table class="table table-fixed table-striped table-bordered">
                    <tbody>
                    <tr v-for="(value, name) in option" v-if="value">
                      <td>{{ getPrettyName(name) }}</td>
                      <td v-if="getType(name) === 'date'">
                        {{ value | toDate(DateFormatEnum.DISPLAY_DATE) }}
                      </td>
                      <td v-else-if="getType(name) === 'boolean'">
                        {{ value ? 'Ja' : 'Nee' }}
                      </td>
                      <td v-else-if="getType(name) === 'euro'">
                        € {{ value }}
                      </td>
                      <td v-else-if="getType(name) === 'percentage'">
                        {{ value * 100 }} %
                      </td>
                      <td v-else-if="getType(name) === 'image'">
                        <lazy-component>
                          <object :data="value" :alt='`Foto ${name}`' style="max-height:50px; max-width:100px">
                            Geen afbeelding gevonden
                          </object>
                        </lazy-component>
                      </td>
                      <td v-else>
                        {{ value }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                  <template #footer>
                    <a-button @click="$set(allDataModal, key, false)">Sluiten</a-button>
                  </template>
                </a-modal>
                <a-modal :visible="errorModal[key]"
                         @cancel="$set(errorModal, key, false)"
                         v-if="option.hasOwnProperty(resultErrorsKey)"
                >
                  <ul>
                    <li v-for="error in option[resultErrorsKey]">
                      {{ errorDescriptionKey ? error[errorDescriptionKey] : error }}
                    </li>
                  </ul>
                  <template #footer>
                    <a-button @click="$set(errorModal, key, false)">Sluiten</a-button>
                  </template>
                </a-modal>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div v-else>
      Laden...
    </div>
  </div>
</template>

<script>
import compareApi from '~/api/compare.js';
import compare_helpers from './compare_helpers';
import EditableValue from './EditableValue';
import moment from 'moment';
import {DateFormatEnum} from "~/enums";

export default {
  name: "CompareComponent",
  props: {
    compareType: {
      type: String,
      validator(value) {
        return ['car', 'death', 'mortgage'].includes(value);
      },
      required: true
    },
    routeParams: {
      type: Object
    },
    title: {
      type: String
    },
    attributeConfig: {
      type: Object,
      default: () => {
      }
    },
    updateOverridesBeforeCompare: {
      type: Function,
      default: null
    },
    showResultTable: {
      type: Boolean,
      default: false
    },
    tableHeaders: {
      type: Array
    },
    resultKeyForTable: {
      type: String
    },
    sortResultBy: {
      type: String,
      default: null
    },
    sortResultFunction: {
      type: Function,
      default: null
    },
    resultErrorsKey: {
      type: String,
      default: 'errors'
    },
    errorDescriptionKey: {
      type: String,
      default: null
    },
    sortDirection: {
      type: String,
      validate: value => ['asc', 'desc'].includes(value),
      default: 'asc'
    }
  },
  mixins: {
    compare_helpers
  },
  components: {
    EditableValue
  },
  data() {
    return {
      allDataModal: {},
      errorModal: {},
      calculationValues: {},
      overrides: {},
      result: null,
      errors: {},
      errorMessage: null,
      loadingResult: false,
      loading: false,
      noResultsFound: false,
      notSearchedYet: true,
      loadingSaving: false,
      resultSaved: false,
      hasLastSavedResult: false,
      REQUIRED_FIELDS: 'required_fields',
      NOT_EDITABLE_FIELDS: 'not_editable_fields',
      previousOverrides: {},
      resultCorrespondsWithOverrides: true,
      DateFormatEnum
    }
  },
  methods: {
    async getCalculationValues() {
      this.loading = true;

      let response = await compareApi.getCalculationValues(this.compareType, this.routeParams);

      this.calculationValues = response.calculationValues;
      if (response.hasOwnProperty('lastSavedResult')) {
        this.resultCorrespondsWithOverrides = false;
        this.result = response.lastSavedResult;
        this.resultSaved = true;
        this.$emit('result-received', this.result);
        this.hasLastSavedResult = true;
      }

      this.loading = false;
    },
    async compare() {
      this.loadingResult = true;

      if (this.updateOverridesBeforeCompare) {
        this.overrides = this.updateOverridesBeforeCompare(this.overrides, this.calculationValues);
      }

      this.previousOverrides = {...this.overrides};

      let response;

      try {
        response = await compareApi.compare(this.compareType, this.routeParams, this.overrides);

        this.result = response.data;
        this.resultCorrespondsWithOverrides = true;
        this.$emit('result-received', this.result);
        this.hasLastSavedResult = false;
        this.noResultsFound = false;
        this.errors = {};
        this.errorMessage = null;
      } catch (e) {
        let responseData = e.response.data;
        if (responseData.error.code === 422) {
          this.errorMessage = responseData.error.message;
          this.errors = responseData.error.errors;
        }

        this.result = null;
        this.noResultsFound = true;
      } finally {
        this.loadingResult = false;
        this.resultSaved = false;
      }

    },
    async save() {
      this.loadingSaving = true;

      try {
        await compareApi.save(this.compareType, this.routeParams, this.previousOverrides);

        this.$notification['success']({
          message: 'Opgeslagen',
          description: 'Het resultaat is opgeslagen',
        });
        this.resultSaved = true;
      } catch (e) {
        this.$notification['warning']({
          message: 'Mislukt',
          description: 'Het opslaan is mislukt.',
        });
      } finally {
        this.loadingSaving = false;
      }
    },
    getOverridesValue(nestedAttributeNamesString) {
      let nestedAttributeNamesArray = nestedAttributeNamesString.split('.');

      const nestedObject = this.getNestedObjectAttribute(this.overrides, false, nestedAttributeNamesArray);

      if (nestedObject === null) return null;

      return nestedObject[nestedAttributeNamesArray[nestedAttributeNamesArray.length - 1]];
    },
    editOverridesValue(newValue, nestedAttributeNamesString) {
      let nestedAttributeNamesArray = nestedAttributeNamesString.split('.');

      const nestedObject = this.getNestedObjectAttribute(this.overrides, true, nestedAttributeNamesArray);

      this.$set(nestedObject, nestedAttributeNamesArray[nestedAttributeNamesArray.length - 1], newValue);
    },
    getNestedObjectAttribute(object, createIfAbsent, nestedAttributeNamesArray) {
      for (const nestedAttributeName of nestedAttributeNamesArray.slice(0, nestedAttributeNamesArray.length - 1)) {
        if (!object.hasOwnProperty(nestedAttributeName) || object[nestedAttributeName] === null) {
          if (createIfAbsent) {
            this.$set(object, nestedAttributeName, {});
          } else {
            return null;
          }
        }

        object = object[nestedAttributeName];
      }

      return object;
    },
    getPrettyName(attribute) {
      if (this.attributeConfig.hasOwnProperty(attribute) && this.attributeConfig[attribute].hasOwnProperty("prettyName")) {
        return this.attributeConfig[attribute].prettyName;
      }
      return attribute;
    },
    getEnumOptions(attribute) {
      if (this.attributeConfig.hasOwnProperty(attribute) && this.attributeConfig[attribute].hasOwnProperty("enumOptions")) {
        return this.attributeConfig[attribute].enumOptions;
      }
      return null;
    },
    getType(attribute) {
      if (this.attributeConfig.hasOwnProperty(attribute) && this.attributeConfig[attribute].hasOwnProperty("type")) {
        return this.attributeConfig[attribute].type;
      }
      return null;
    },
    getErrors(calculationValueName) {
      let camelCase = _.camelCase(calculationValueName);
      let pascalCase = camelCase[0].toUpperCase() + camelCase.slice(1);

      if (this.errors.hasOwnProperty(calculationValueName)) {
        return this.errors[calculationValueName];
      } else if (this.errors.hasOwnProperty(pascalCase)) {
        return this.errors[pascalCase];
      } else {
        return null;
      }
    },
    pascalCase(str) {
      const camelCase = _.camelCase(str);

      return camelCase.charAt(0).toUpperCase() + camelCase.slice(1);
    },
    getCollection(collection) {
      if (collection == null) return [];

      return _.pickBy(collection, (value, key) => key !== this.REQUIRED_FIELDS && key !== this.NOT_EDITABLE_FIELDS)
    },
    getSimplesFromCollection(collection) {
      return _.pickBy(this.getCollection(collection), (value) => (value == null || typeof value !== "object"));
    },
    getObjectsFromCollection(collection) {
      return _.pickBy(this.getCollection(collection), (value) => value != null && value instanceof Object && !(value instanceof Array));
    },
    getArraysFromCollection(collection) {
      return _.pickBy(this.getCollection(collection), (value) => value != null && value instanceof Array);
    },
    getRequiredFields(collection) {
      if (collection && collection.hasOwnProperty(this.REQUIRED_FIELDS)) {
        return collection[this.REQUIRED_FIELDS];
      } else {
        return [];
      }
    },
    getNotEditableFields(collection) {
      if (collection && collection.hasOwnProperty(this.NOT_EDITABLE_FIELDS)) {
        return collection[this.NOT_EDITABLE_FIELDS];
      } else {
        return [];
      }
    }
  },
  computed: {
    getTableResult() {
      let result = _.get(this.result, this.resultKeyForTable);

      if (!result) {
        return null;
      }

      if (this.sortResultFunction) {
        return this.sortResultFunction(result);
      }

      if (this.sortResultBy) {
        let premiumSorted = result.sort((a, b) => {
          if (a.hasOwnProperty(this.sortResultBy) && b.hasOwnProperty(this.sortResultBy)) {
            if (this.sortDirection === 'desc') {
              return b[this.sortResultBy] - a[this.sortResultBy];
            } else {
              return a[this.sortResultBy] - b[this.sortResultBy];
            }

          } else {
            return 1;
          }
        })

        if (this.resultErrorsKey) {
          return premiumSorted.sort((a, b) => {
            return a[this.resultErrorsKey] && a[this.resultErrorsKey].length > 0
                ? 1 : -1;
          })
        } else {
          return premiumSorted;
        }
      }

      return null;
    },
    getResultCreatedAt() {
      if (!this.getTableResult || this.getTableResult.length === 0 || !this.getTableResult[0].hasOwnProperty('created_at')) {
        return null;
      } else {
        return moment(this.getTableResult[0].created_at).fromNow();
      }
    },
    hasResultsWithoutIssues() {
      return this.getTableResult ? this.getTableResult.filter(result => !result[this.resultErrorsKey] || result[this.resultErrorsKey].length === 0).length > 0 : false;
    }
  },
  watch: {
    overrides: {
      handler: function (newValue, oldValue) {
        this.resultCorrespondsWithOverrides = false;
      },
      deep: true
    }
  },
  async beforeMount() {
    await this.getCalculationValues();

    this.$emit('mounted', this.calculationValues);
  },
}
</script>

<style scoped>

</style>
